import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { captureError } from "@/lib/sentry";
import { LightningIcon, CaretDownIcon, ChatCircleDotsIcon, BookmarkSimpleIcon, CheckIcon, TrendUpIcon, TrendDownIcon } from "@phosphor-icons/react";

function PlayRow({ gameId, play, language, onAskGamly }) {
  const [open, setOpen] = useState(false);
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const expand = async () => {
    const next = !open;
    setOpen(next);
    if (next && !insight && !loading) {
      setLoading(true);
      try {
        const r = await api.post(`/games/${gameId}/play-insight`, { description: play.description, language });
        setInsight(r.data);
      } catch (err) {
        captureError("play insight failed:", err);
        setInsight({ gamly: "Couldn't generate right now.", fantasy_impact: "", card_impact: "" });
      } finally {
        setLoading(false);
      }
    }
  };

  const save = async () => {
    try {
      await api.post("/highlights", { game_id: gameId, kind: "play", title: play.description, detail: play.period });
      setSaved(true);
    } catch (err) { captureError("save play highlight failed:", err); }
  };

  return (
    <div className={`rounded-md border ${play.is_scoring ? "bg-[#32D74B]/10 border-[#32D74B]/30" : "bg-[#121212] border-white/10"}`} data-testid={`feed-play-${play.id}`}>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-num text-[10px] font-black text-white/40 w-12">{play.period}</span>
          {play.momentum !== 0 && (
            <span className={`inline-flex items-center gap-0.5 text-[10px] font-black uppercase ${play.momentum > 0 ? "text-[#32D74B]" : "text-[#FF453A]"}`}>
              {play.momentum > 0 ? <TrendUpIcon size={12} /> : <TrendDownIcon size={12} />}
              {Math.abs(play.momentum)}% {play.momentum > 0 ? play.home_abbr : play.away_abbr}
            </span>
          )}
        </div>
        <p className={`text-sm leading-snug ${play.is_scoring ? "text-[#32D74B] font-semibold" : "text-white/85"}`}>{play.description}</p>

        {/* Win probability bar */}
        <div className="mt-2.5">
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1">
            <span>Win Prob</span>
            <span>{play.away_abbr} {play.win_prob_away}% · {play.home_abbr} {play.win_prob_home}%</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden bg-white/5 flex">
            <div className="h-full bg-[#FF9F0A]" style={{ width: `${play.win_prob_away}%` }} />
            <div className="h-full bg-[#0A84FF]" style={{ width: `${play.win_prob_home}%` }} />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <button onClick={expand} data-testid={`feed-expand-${play.id}`} className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#0A84FF] hover:text-blue-400">
            <CaretDownIcon size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} /> Gamly Breakdown
          </button>
          <button onClick={() => onAskGamly?.()} data-testid={`feed-ask-${play.id}`} className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white/55 hover:text-white"><ChatCircleDotsIcon size={12} /> Ask</button>
          <button onClick={save} data-testid={`feed-save-${play.id}`} className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider ${saved ? "text-[#32D74B]" : "text-white/55 hover:text-white"}`}>
            {saved ? <CheckIcon size={12} /> : <BookmarkSimpleIcon size={12} />} {saved ? "Saved" : "Save"}
          </button>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4 border-t border-white/5 pt-3 space-y-2" data-testid={`feed-insight-${play.id}`}>
          {loading ? (
            <p className="text-xs text-white/50">Gamly analyzing…</p>
          ) : (
            <>
              <div className="flex gap-1.5">
                <LightningIcon weight="fill" size={14} className="text-[#0A84FF] shrink-0 mt-0.5" />
                <p className="text-xs text-white/85 leading-relaxed">{insight?.gamly}</p>
              </div>
              {insight?.fantasy_impact && <p className="text-xs text-white/65"><span className="font-bold uppercase tracking-wider text-[#FDB927] text-[10px]">Fantasy · </span>{insight.fantasy_impact}</p>}
              {insight?.card_impact && <p className="text-xs text-white/65"><span className="font-bold uppercase tracking-wider text-[#A855F7] text-[10px]">Cards · </span>{insight.card_impact}</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function PlayFeed({ gameId, gameStatus, language = "en", onAskGamly }) {
  const [plays, setPlays] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/games/${gameId}/play-feed`);
      setPlays(r.data.plays || []);
    } catch (err) {
      captureError("play feed load failed:", err);
    } finally {
      setLoaded(true);
    }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (gameStatus !== "LIVE") return undefined;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [gameStatus, load]);

  if (loaded && plays.length === 0) {
    return <div className="p-6 rounded-md bg-[#121212] border border-white/10 text-white/50 text-sm" data-testid="play-feed-empty">No plays yet — check back once the game is underway.</div>;
  }

  return (
    <div className="space-y-3" data-testid="play-feed">
      {plays.map((p) => <PlayRow key={p.id} gameId={gameId} play={p} language={language} onAskGamly={onAskGamly} />)}
    </div>
  );
}
